import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Rating,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Avatar,
  Chip,
  Container,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedIcon from '@mui/icons-material/Verified';
import { format } from 'date-fns';
import { getReviews, addReviewReply, updateReviewReply, deleteReviewReply } from '@services/reviewService';
import { getStylists } from '@services/stylistService';
import { toast } from 'react-toastify';
import StylistSelector from '@components/StylistSelector';
import LoadingSpinner from '@components/LoadingSpinner';
import type { Review, ReviewReply } from '@app-types/models/review';
import type { Stylist } from '@app-types/models/stylist';

interface SelectedReview extends Review {
  reply: ReviewReply;
}

interface ApiError {
  message?: string;
}

const ReviewsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [selectedReview, setSelectedReview] = useState<SelectedReview | null>(null);
  const [replyTexts, setReplyTexts] = useState<Record<string, string>>({});
  const [editDialogText, setEditDialogText] = useState<string>('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [selectedStylist, setSelectedStylist] = useState<string | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'needsAttention'>('all');
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingReviews, setIsLoadingReviews] = useState<boolean>(false);

  useEffect(() => {
    const fetchStylists = async (): Promise<void> => {
      try {
        const stylistsResult = await getStylists();
        const stylistsData = Array.isArray(stylistsResult) ? stylistsResult : [];
        setStylists(stylistsData);
        if (stylistsData.length > 0) {
          setSelectedStylist(stylistsData[0]._id);
        }
      } catch (error: unknown) {
        console.error('Error fetching stylists:', error);
        toast.error(t('common.errorLoadingData'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStylists();
  }, [t]);

  useEffect(() => {
    const fetchReviews = async (): Promise<void> => {
      if (!selectedStylist) return;

      try {
        setIsLoadingReviews(true);
        const reviewsResponse = await getReviews(selectedStylist);
        setReviews(Array.isArray(reviewsResponse.reviews) ? reviewsResponse.reviews : []);
      } catch (error: unknown) {
        console.error('Error fetching reviews:', error);
        toast.error(t('common.errorLoadingData'));
      } finally {
        setIsLoadingReviews(false);
      }
    };

    fetchReviews();
  }, [selectedStylist, t]);

  const handleStylistSelect = (stylistId: string): void => {
    setSelectedStylist(stylistId);
  };

  const handleSubmitReply = async (reviewId: string): Promise<void> => {
    if (!replyTexts[reviewId]?.trim()) return;
    try {
      await addReviewReply({ reviewId, reply: replyTexts[reviewId], staffId: selectedStylist! });
      const updatedReviewsResponse = await getReviews(selectedStylist!);
      setReviews(Array.isArray(updatedReviewsResponse.reviews) ? updatedReviewsResponse.reviews : []);
      setReplyTexts(prev => {
        const newTexts = { ...prev };
        delete newTexts[reviewId];
        return newTexts;
      });
      toast.success(t('reviews.replyAdded'));
    } catch (error: unknown) {
      toast.error((error as ApiError).message || t('reviews.errorAddingReply'));
    }
  };

  const handleSubmitEdit = async (): Promise<void> => {
    if (!editDialogText?.trim()) return;
    try {
      await updateReviewReply({ reviewId: selectedReview!._id, replyId: selectedReview!.reply._id, reply: editDialogText, staffId: selectedStylist! });
      const updatedReviewsResponse2 = await getReviews(selectedStylist!);
      setReviews(Array.isArray(updatedReviewsResponse2.reviews) ? updatedReviewsResponse2.reviews : []);
      setIsEditDialogOpen(false);
      setEditDialogText('');
      toast.success(t('reviews.replyUpdated'));
    } catch (error: unknown) {
      toast.error((error as ApiError).message || t('reviews.errorUpdatingReply'));
    }
  };

  const handleDeleteReply = async (reviewId: string, replyId: string): Promise<void> => {
    if (window.confirm(t('reviews.confirmDeleteReply'))) {
      try {
        await deleteReviewReply({ reviewId, replyId, staffId: selectedStylist! });
        const updatedReviewsResponse3 = await getReviews(selectedStylist!);
        setReviews(Array.isArray(updatedReviewsResponse3.reviews) ? updatedReviewsResponse3.reviews : []);
        toast.success(t('reviews.replyDeleted'));
      } catch (error: unknown) {
        toast.error((error as ApiError).message || t('reviews.errorDeletingReply'));
      }
    }
  };

  const handleEditReply = (review: Review, reply: ReviewReply): void => {
    setSelectedReview({ ...review, reply });
    setEditDialogText(reply.content);
    setIsEditDialogOpen(true);
  };

  const handleReplyTextChange = (reviewId: string, value: string): void => {
    setReplyTexts(prev => ({
      ...prev,
      [reviewId]: value
    }));
  };

  const handleEditDialogTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setEditDialogText(e.target.value);
  };

  const needsAttentionCount = reviews.filter((review) => review.status === 'pending-recovery').length;
  const displayedReviews = reviewFilter === 'needsAttention'
    ? reviews.filter((review) => review.status === 'pending-recovery')
    : reviews;

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Container>
      <StylistSelector
        stylists={stylists}
        selectedStylistId={selectedStylist ?? undefined}
        onStylistSelect={(value) => handleStylistSelect(value as string)}
        layout="scroll"
        showEmail={true}
        variant="id"
      />

      <ToggleButtonGroup
        value={reviewFilter}
        exclusive
        onChange={(_, value) => value && setReviewFilter(value)}
        size="small"
        sx={{ mt: 3 }}
      >
        <ToggleButton value="all">
          {t('reviews.filterAll')}
        </ToggleButton>
        <ToggleButton value="needsAttention">
          {t('reviews.filterNeedsAttention')} ({needsAttentionCount})
        </ToggleButton>
      </ToggleButtonGroup>

      <Stack spacing={3} sx={{ mt: 3 }}>
        {isLoadingReviews ? (
          <LoadingSpinner />
        ) : (
          displayedReviews.map((review) => (
            <Card key={review._id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2 }}>
                      {review?.user?.displayName?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">{review?.user?.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(review?.createdAt ?? ''), 'PPp')}
                      </Typography>
                    </Box>
                  </Box>
                  {review.reservationId && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      backgroundColor: 'grey.50',
                      p: 1.5,
                      borderRadius: 1,
                      minWidth: '200px'
                    }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('reservation.reservedSlot')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {format(new Date(review.reservationId.start), 'PPp')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {i18n.language === 'zh' ? review.reservationId.planId.zh_name : review.reservationId.planId.name}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                  <Rating value={review.rating} readOnly />
                  {review.verifiedVisit && (
                    <Chip
                      icon={<VerifiedIcon />}
                      label={t('reviews.verifiedVisit')}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {review.status === 'pending-recovery' && (
                    <Chip
                      label={t('reviews.needsAttentionBadge')}
                      size="small"
                      color="warning"
                    />
                  )}
                </Stack>
                <Typography variant="body1" paragraph>
                  {review.comment}
                </Typography>

                {review.highlights && review.highlights.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {review.highlights.map((highlight, index) => (
                      <Chip
                        key={index}
                        label={highlight}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}

                {review.replies && review.replies.length > 0 ? (
                  <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                    {review.replies.map((reply) => (
                      <Box key={reply._id} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center'}}>
                          <Typography variant="subtitle2" sx={{ mr: 1 }}>
                            {reply.user ? t('reviews.userReply') : t('reviews.yourReply')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(reply?.updatedAt ?? ''), 'PPp')}
                          </Typography>
                          {!reply.user && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEditReply(review, reply)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteReply(review._id, reply._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                        <Typography variant="body2">{reply.content}</Typography>
                      </Box>
                    ))}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder={t('reviews.replyPlaceholder')}
                        fullWidth
                        value={replyTexts[review._id] || ''}
                        onChange={(e) => handleReplyTextChange(review._id, e.target.value)}
                        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmitReply(review._id);
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => handleSubmitReply(review._id)}
                        disabled={!replyTexts[review._id]?.trim()}
                        sx={{ minWidth: '100px' }}
                      >
                        {t('reviews.reply')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder={t('reviews.replyPlaceholder')}
                      fullWidth
                      value={replyTexts[review._id] || ''}
                      onChange={(e) => handleReplyTextChange(review._id, e.target.value)}
                      onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmitReply(review._id);
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => handleSubmitReply(review._id)}
                      disabled={!replyTexts[review._id]?.trim()}
                      sx={{ minWidth: '100px' }}
                    >
                      {t('reviews.reply')}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </Stack>

      {/* Edit Reply Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)}>
        <DialogTitle>{t('reviews.editReply')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('reviews.replyText')}
            fullWidth
            multiline
            rows={4}
            value={editDialogText}
            onChange={handleEditDialogTextChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            disabled={!editDialogText?.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReviewsPage;
